import { calcUnappliedCents, withTotals } from "@/features/invoices/lib/invoice-calc";
import type { Invoice, InvoiceLineItem, InvoicePayment, LedgerRow, Payment } from "@/features/invoices/types";

/** The slice of raw store state these selectors need — kept as plain args (not `get()`) so they're testable with plain arrays. */
export interface InvoiceStateSlice {
  invoices: Invoice[];
  lineItems: InvoiceLineItem[];
  payments: Payment[];
  invoicePayments: InvoicePayment[];
}

/** Merged invoice+payment ledger for one client, sorted by date desc (matches the legacy invoice_manager.php list). */
export function getInvoiceLedgerForClient(clientId: string, state: InvoiceStateSlice, asOf?: string): LedgerRow[] {
  const invoiceRows: LedgerRow[] = state.invoices
    .filter((inv) => inv.clientId === clientId)
    .map((inv) => {
      const items = state.lineItems.filter((li) => li.invoiceId === inv.id);
      return {
        kind: "invoice" as const,
        date: inv.invoiceDate,
        invoice: withTotals(inv, items, state.invoicePayments, asOf),
      };
    });

  const paymentRows: LedgerRow[] = state.payments
    .filter((p) => p.clientId === clientId)
    .map((p) => {
      const unappliedCents = calcUnappliedCents(p, state.invoicePayments);
      return {
        kind: "payment" as const,
        date: p.paymentDate,
        payment: { ...p, appliedCents: p.paymentAmtCents - unappliedCents, unappliedCents },
      };
    });

  return [...invoiceRows, ...paymentRows].sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Client-level totals row — excludes VOID (cancelled) and DRAFT (not yet
 * committed) invoices from `amountCents`/`paidCents`, matching legacy's
 * totals calc.
 *
 * `balanceCents` (net accounts receivable, feeds the "Total Due" footer) is
 * `amountCents` minus *every* payment for the client, not just the portion
 * applied to invoices — an unapplied credit still reduces what the client
 * owes overall even before it's allocated to a specific invoice. This
 * mirrors the legacy list page's running total, which subtracts each
 * payment's full amount (not just its applied portion) from the invoice
 * total: sum(invoice balances) - sum(unapplied credits) reduces to
 * sum(invoice totals) - sum(all payments).
 */
export function getClientInvoiceTotals(clientId: string, state: InvoiceStateSlice, asOf?: string) {
  const committed = state.invoices.filter(
    (inv) => inv.clientId === clientId && inv.status !== "VOID" && inv.status !== "DRAFT",
  );

  const { amountCents, paidCents } = committed.reduce(
    (totals, inv) => {
      const items = state.lineItems.filter((li) => li.invoiceId === inv.id);
      const withT = withTotals(inv, items, state.invoicePayments, asOf);
      return {
        amountCents: totals.amountCents + withT.totalCents,
        paidCents: totals.paidCents + withT.appliedCents,
      };
    },
    { amountCents: 0, paidCents: 0 },
  );

  const totalPaymentsCents = state.payments
    .filter((p) => p.clientId === clientId)
    .reduce((sum, p) => sum + p.paymentAmtCents, 0);

  return { amountCents, paidCents, balanceCents: amountCents - totalPaymentsCents };
}
